import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { uploadBookingFile, uploadBookingText, getBookingFiles, openAuthenticatedDownload, api } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Warning, File as FileIcon, Trash, DownloadSimple, UploadSimple, PaperPlaneTilt, FileText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ALL_OMCS = ['Bharat Gas', 'HPCL', 'Indane'];

export default function DistributorSubmitBooking() {
  const { user } = useAuth();
  const [bookingFiles, setBookingFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bfUploading, setBfUploading] = useState(false);
  const [allowedOmcs, setAllowedOmcs] = useState(ALL_OMCS);
  const [bfOilCompany, setBfOilCompany] = useState('');
  const [bfNote, setBfNote] = useState('');
  const [bfMode, setBfMode] = useState('single');
  const [bfTextContent, setBfTextContent] = useState('');
  const [bfSelectedFile, setBfSelectedFile] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [filesRes, omcsRes] = await Promise.all([
        getBookingFiles(),
        api.get('/distributor/my-omcs').catch(() => ({ data: { omcs: ALL_OMCS } })),
      ]);
      setBookingFiles(filesRes.data);
      const omcs = (omcsRes.data?.omcs || []).filter(o => ALL_OMCS.includes(o));
      const finalOmcs = omcs.length > 0 ? omcs : ALL_OMCS;
      setAllowedOmcs(finalOmcs);
      // Auto-select first OMC if not yet picked OR current selection invalid
      setBfOilCompany(prev => (prev && finalOmcs.includes(prev)) ? prev : finalOmcs[0]);
    } catch (err) {
      toast.error('Data load nahi hua');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleBookingTextSubmit = async () => {
    if (!bfTextContent.trim()) { toast.error('Text content daalo'); return; }
    setBfUploading(true);
    try {
      await uploadBookingText({ oil_company: bfOilCompany, text_content: bfTextContent, note: bfNote });
      toast.success('Booking text submitted!');
      setBfTextContent('');
      setBfNote('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit fail hua');
    } finally {
      setBfUploading(false);
    }
  };

  const handleReupload = async (bfId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const apiBase = window.location.origin;
      const url = `${apiBase}/api/booking-files/${bfId}/reupload`;
      const res = await fetch(url, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error((await res.json())?.detail || 'Re-upload fail hua');
      toast.success('File re-uploaded successfully!');
      fetchData();
    } catch (err) {
      toast.error(err.message || 'Re-upload fail hua');
    }
  };

  const handleBookingFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBfSelectedFile(file);
  };

  const handleBookingFileUpload = async () => {
    if (!bfSelectedFile) { toast.error('Pehle file select karo'); return; }
    setBfUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', bfSelectedFile);
      fd.append('oil_company', bfOilCompany);
      fd.append('note', bfNote);
      await uploadBookingFile(fd);
      toast.success('Booking file uploaded!');
      setBfNote('');
      setBfSelectedFile(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fail hua');
    } finally {
      setBfUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const sampleContent = [
      '# Sample Booking Template',
      '# Format: AGENCY NAME = QUANTITY',
      '# Ek line me ek agency ka data likhein',
      '# Lines starting with # are ignored',
      '',
      'AJAY BHARAT GAS = 100',
      'KAUSHIKI BHARATGAS = 50',
      'KISHORI INDANE = 20',
      'SHARMA HP GAS = 75',
      'VERMA BHARAT GAS = 30',
    ].join('\n');
    const blob = new Blob([sampleContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'booking_template_sample.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Sample template downloaded!');
  };

  const bfStatusLabel = (s) => {
    const map = { pending: 'Pending', assigned: 'Assigned', processing: 'Processing', completed: 'Completed', failed: 'Failed' };
    return map[s] || s;
  };
  const bfStatusColor = (s) => {
    const map = { pending: 'bg-amber-100 text-amber-700', assigned: 'bg-blue-100 text-blue-700', processing: 'bg-purple-100 text-purple-700', completed: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  const formatCurrency = (val) => {
    if (val == null) return '₹0';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(val);
  };

  if (loading) {
    return <div className="p-6 flex justify-center items-center min-h-[50vh]"><div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="distributor-submit-booking">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Submit Booking Data</h1>
          <p className="text-sm text-gray-500 mt-0.5">Oil company select karein, data enter karein aur submit karein</p>
        </div>
        <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 self-start sm:self-auto" data-testid="download-sample-template-btn">
          <FileText size={18} /> Sample Template
        </Button>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="bf-warning-notice">
        <Warning size={18} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">Submit hone ke baad booking data <strong>cancel nahi hoga</strong>. Pehle verify kar lein.</p>
      </div>

      {/* Submit Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 space-y-4" data-testid="upload-booking-file-section">
        {/* Oil Company Selection */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">
            Select Oil Company
            {allowedOmcs.length === 1 && (
              <span className="text-[11px] font-normal text-emerald-700 ml-2">(auto-detected from your agency)</span>
            )}
          </Label>
          <div className="grid grid-cols-3 gap-2">
            {ALL_OMCS.map(c => {
              const isAllowed = allowedOmcs.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  disabled={!isAllowed}
                  onClick={() => isAllowed && setBfOilCompany(c)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                    !isAllowed
                      ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                      : bfOilCompany === c
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                  title={!isAllowed ? `Aapki kisi agency ka OMC '${c}' nahi hai` : ''}
                  data-testid={`bf-oil-${c.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {c}
                  {!isAllowed && <span className="ml-1 text-[10px]">🚫</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Instructions based on Oil Company */}
        <div className={`rounded-lg p-3 ${
          bfOilCompany === 'Indane'
            ? 'bg-blue-50 border border-blue-200'
            : 'bg-purple-50 border border-purple-200'
        }`} data-testid="bf-instructions">
          {bfOilCompany === 'Indane' ? (
            <p className="text-xs text-blue-800 leading-relaxed">
              <span className="font-semibold">Indane:</span> LPG ID ya Consumer Number dalein. Ek line me ek entry. Format: <span className="font-mono bg-blue-100 px-1 rounded">AGENCY NAME = QUANTITY</span>
            </p>
          ) : (
            <p className="text-xs text-purple-800 leading-relaxed">
              <span className="font-semibold">{bfOilCompany}:</span> Mobile Number dalein ya Excel file upload karein. Ek line me ek entry. Format: <span className="font-mono bg-purple-100 px-1 rounded">AGENCY NAME = QUANTITY</span>
            </p>
          )}
        </div>

        {/* Entry Mode Selector */}
        <div>
          <Label className="text-sm font-medium text-gray-700 mb-2 block">Entry Mode</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setBfMode('single')}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                bfMode === 'single'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
              data-testid="bf-mode-single"
            >
              <PaperPlaneTilt size={16} />
              {bfOilCompany === 'Indane' ? 'LPG ID / Consumer No.' : 'Mobile Number'}
            </button>
            <button
              type="button"
              onClick={() => setBfMode('bulk_file')}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                bfMode === 'bulk_file'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
              }`}
              data-testid="bf-mode-bulk"
            >
              <UploadSimple size={16} /> Excel / File Upload
            </button>
          </div>
        </div>

        {/* Input Area */}
        {bfMode === 'single' ? (
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
              {bfOilCompany === 'Indane' ? 'Enter LPG ID / Consumer Number' : 'Enter Mobile Numbers'}
            </Label>
            <textarea
              value={bfTextContent}
              onChange={e => setBfTextContent(e.target.value)}
              placeholder={'Enter one entry per line\n\nAGENCY NAME = QUANTITY'}
              rows={6}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm resize-y focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none font-mono"
              data-testid="bf-text-content"
            />
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-5 text-center bg-gray-50" data-testid="bf-file-drop-zone">
            {bfSelectedFile ? (
              <div className="space-y-2">
                <FileIcon size={28} className="text-emerald-600 mx-auto" />
                <p className="text-sm font-medium text-gray-800">{bfSelectedFile.name}</p>
                <p className="text-xs text-gray-500">{(bfSelectedFile.size / 1024).toFixed(1)} KB</p>
                <Button variant="ghost" size="sm" onClick={() => setBfSelectedFile(null)} className="text-red-500 hover:text-red-700 text-xs">
                  <Trash size={14} className="mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <>
                <UploadSimple size={28} className="text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-1 font-medium">Upload Excel or Text File</p>
                <p className="text-xs text-gray-400 mb-3">Supported: .xlsx, .csv, .txt (max 10MB)</p>
                <div className="relative inline-block">
                  <input type="file" accept=".xlsx,.xls,.csv,.txt" onChange={handleBookingFileSelect} disabled={bfUploading} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-testid="bf-file-input" />
                  <Button variant="outline" size="sm" className="gap-2" disabled={bfUploading}>
                    <UploadSimple size={16} /> Choose File
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Row 3: Note + Submit */}
        <div className="flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1 w-full">
            <Label className="text-xs text-gray-500 mb-1 block">Note (Optional)</Label>
            <Input value={bfNote} onChange={e => setBfNote(e.target.value)} placeholder="e.g. April booking list" className="text-sm" data-testid="bf-note-input" />
          </div>
          {bfMode === 'single' ? (
            <Button onClick={handleBookingTextSubmit} className="bg-emerald-600 hover:bg-emerald-700 gap-2 w-full sm:w-auto" disabled={bfUploading || !bfTextContent.trim()} data-testid="bf-submit-btn">
              <PaperPlaneTilt size={16} /> {bfUploading ? 'Submitting...' : 'Submit'}
            </Button>
          ) : (
            <Button onClick={handleBookingFileUpload} className="bg-emerald-600 hover:bg-emerald-700 gap-2 w-full sm:w-auto" disabled={bfUploading || !bfSelectedFile} data-testid="bf-upload-btn">
              <PaperPlaneTilt size={16} /> {bfUploading ? 'Uploading...' : 'Submit File'}
            </Button>
          )}
        </div>
      </div>

      {/* Recent Booking Files */}
      {bookingFiles.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5" data-testid="booking-files-list">
          <h2 className="text-base sm:text-lg font-semibold mb-3">Recent Submissions</h2>
          {/* Mobile card view */}
          <div className="sm:hidden space-y-3">
            {bookingFiles.slice(0, 10).map(bf => (
              <div key={bf.id} className="border border-gray-100 rounded-lg p-3 space-y-2">
                <div className="flex justify-between items-start">
                  <div>
                    <span className={`text-xs px-2 py-0.5 rounded ${bf.entry_mode === 'single' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                      {bf.entry_mode === 'single' ? 'Text' : 'File'}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded ml-1">{bf.oil_company}</span>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${bfStatusColor(bf.status)}`}>{bfStatusLabel(bf.status)}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Upload: {formatDateTime(bf.created_at)}</span>
                  {bf.total_amount_debited > 0 && <span className="font-medium text-gray-800">{formatCurrency(bf.total_amount_debited)}</span>}
                </div>
                {bf.assigned_to_name && (
                  <div className="text-xs text-gray-500">
                    Assigned: {bf.assigned_to_name} {bf.assigned_at ? `(${formatDateTime(bf.assigned_at)})` : ''}
                  </div>
                )}
                {bf.file_available === false ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 font-medium" data-testid={`file-unavailable-${bf.id}`}>File unavailable</span>
                    <label className="text-xs text-blue-600 hover:underline cursor-pointer flex items-center gap-1" data-testid={`reupload-btn-mobile-${bf.id}`}>
                      <DownloadSimple size={12} style={{ transform: 'rotate(180deg)' }} /> Re-upload
                      <input type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden"
                        onChange={(e) => { const f = e.target.files[0]; if (f) handleReupload(bf.id, f); e.target.value = ''; }} />
                    </label>
                  </div>
                ) : (
                  <button type="button"
                    onClick={() => openAuthenticatedDownload(`/api/booking-files/${bf.id}/download`, bf.original_filename).catch(e => toast.error(e?.response?.data?.detail || 'Download fail hua'))}
                    className="text-blue-600 hover:underline text-xs flex items-center gap-1" data-testid={`download-original-${bf.id}`}>
                    <DownloadSimple size={12} /> {bf.original_filename}
                  </button>
                )}
                {bf.processed_file_url && bf.processed_file_name && (
                  <button type="button"
                    onClick={() => openAuthenticatedDownload(`/api/booking-files/${bf.id}/download-processed`, bf.processed_file_name).catch(e => toast.error(e?.response?.data?.detail || 'Processed download fail hua'))}
                    className="text-green-600 hover:underline text-xs flex items-center gap-1" data-testid={`download-processed-${bf.id}`}>
                    <DownloadSimple size={12} /> Processed: {bf.processed_file_name}
                  </button>
                )}
              </div>
            ))}
          </div>
          {/* Desktop table view */}
          <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Oil Company</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Assigned At</TableHead>
                  <TableHead>Processed</TableHead>
                  <TableHead className="text-right">Debited</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookingFiles.map(bf => (
                  <TableRow key={bf.id}>
                    <TableCell className="text-xs text-gray-600 whitespace-nowrap">{formatDateTime(bf.created_at)}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded ${bf.entry_mode === 'single' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {bf.entry_mode === 'single' ? 'Text' : 'File'}
                      </span>
                    </TableCell>
                    <TableCell><span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{bf.oil_company}</span></TableCell>
                    <TableCell>
                      {bf.file_available === false ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-red-600 font-medium" data-testid={`file-unavailable-row-${bf.id}`}>File unavailable</span>
                          <label className="text-sm text-blue-600 hover:underline cursor-pointer flex items-center gap-1" data-testid={`reupload-btn-${bf.id}`}>
                            <DownloadSimple size={14} style={{ transform: 'rotate(180deg)' }} /> Re-upload
                            <input type="file" accept=".xlsx,.xls,.csv,.txt" className="hidden"
                              onChange={(e) => { const f = e.target.files[0]; if (f) handleReupload(bf.id, f); e.target.value = ''; }} />
                          </label>
                        </div>
                      ) : (
                        <button type="button"
                          onClick={() => openAuthenticatedDownload(`/api/booking-files/${bf.id}/download`, bf.original_filename).catch(e => toast.error(e?.response?.data?.detail || 'Download fail hua'))}
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1" data-testid={`download-original-row-${bf.id}`}>
                          <DownloadSimple size={14} /> {bf.original_filename}
                        </button>
                      )}
                    </TableCell>
                    <TableCell><span className={`text-xs font-medium px-2 py-0.5 rounded ${bfStatusColor(bf.status)}`}>{bfStatusLabel(bf.status)}</span></TableCell>
                    <TableCell className="text-sm">
                      {bf.assigned_to_name ? (
                        <>
                          <div>{bf.assigned_to_name}</div>
                          {bf.reassignment_count > 0 && (
                            <div className="text-[10px] text-amber-700 mt-0.5">🔄 Reassigned ×{bf.reassignment_count}</div>
                          )}
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500 whitespace-nowrap">{bf.assigned_at ? formatDateTime(bf.assigned_at) : '-'}</TableCell>
                    <TableCell className="text-sm">
                      {bf.processed_file_name ? (
                        <button type="button"
                          onClick={() => openAuthenticatedDownload(`/api/booking-files/${bf.id}/download-processed`, bf.processed_file_name).catch(e => toast.error(e?.response?.data?.detail || 'Processed download fail hua'))}
                          className="text-green-600 hover:underline text-xs flex items-center gap-1" data-testid={`download-processed-row-${bf.id}`}>
                          <DownloadSimple size={12} /> {bf.processed_file_name}
                        </button>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-medium text-sm">{bf.total_amount_debited > 0 ? formatCurrency(bf.total_amount_debited) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
